import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { authSnapshot, authKey, dismissalKey, endpoint, KINDS, pendingContracts, validateStatus, zurichDay, deliveryMessage, validateOwner, ownLabFor } from "./digitalContractState";
import "./DigitalContracts.scss";
import SignaturePad from "./SignaturePad";

const DEMO = "TEST – einfache elektronische Signatur. Keine qualifizierte elektronische Signatur (QES). Keine endgültige Vertragsfreigabe im Test.";
const LABELS = { required: "Vertrag vorbereiten", draft: "Entwurf prüfen", approval_pending: "Freigabe ausstehend", approved_test: "Test abgeschlossen – einfache elektronische Signatur", completed: "Einfach elektronisch unterschrieben und freigegeben" };

function DemoDialog({ children, onClose, isTest }) {
  const dialog = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const node = dialog.current;
    node?.querySelector("button")?.focus();
    function keydown(event) {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key === "Tab") {
        const focusable = Array.from(node.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),[tabindex="0"]'));
        const first = focusable[0]; const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    node?.addEventListener("keydown", keydown);
    return () => { node?.removeEventListener("keydown", keydown); if (previous?.isConnected) previous.focus(); };
  }, [onClose]);
  return <div className="digital-contract-backdrop"><section ref={dialog} role="dialog" aria-modal="true" aria-labelledby="digital-contract-title" tabIndex={-1} className="digital-contract-dialog">
    <button className="digital-contract-close" type="button" onClick={onClose} aria-label="Vertrag schliessen">Schliessen</button>
    <h2 id="digital-contract-title">Meine Verträge{isTest ? " · TEST" : ""}</h2>{children}
  </section></div>;
}

export default function DigitalContracts({ page = false }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [consent, setConsent] = useState(false);
  const [signature, setSignature] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [owner, setOwner] = useState(authKey(authSnapshot()));
  const request = useRef(0);
  const mounted = useRef(true);
  const busyRef = useRef(false);
  const lastRefresh = useRef(0);
  const objectUrl = useRef(null);
  const dismissedInMemory = useRef(new Set());
  const mutationKeys = useRef(new Map());
  const auth = authSnapshot();
  const currentOwner = authKey(auth);
  const validState = state?.owner === currentOwner ? state.payload : null;
  const clearPreview = useCallback(() => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null; setPreview(null); setConsent(false); setSignature(null);
  }, []);
  const close = useCallback(() => {
    const current = authSnapshot();
    if (current) {
      dismissedInMemory.current.add(dismissalKey(current));
      try { sessionStorage.setItem(dismissalKey(current), "dismissed"); } catch (_) { /* storage optional */ }
    }
    setOpen(false); clearPreview();
  }, [clearPreview]);
  const live = (key, number) => mounted.current && authKey(authSnapshot()) === key && request.current === number;
  const post = (action, snapshot, data, config = {}) => axios.post(`${endpoint(process.env.REACT_APP_API_BASE_URL)}?action=${action}`, { ...snapshot, own_lab: ownLabFor(snapshot), ...data }, { withCredentials: true, ...config });
  const refresh = useCallback(async (force = false) => {
    if (busyRef.current || (!force && Date.now() - lastRefresh.current < 3000)) return;
    const snapshot = authSnapshot(); const key = authKey(snapshot); const number = ++request.current;
    lastRefresh.current = Date.now();
    if (!snapshot) { setState(null); setOpen(false); return; }
    try {
      const response = await post("status", snapshot, {});
      if (!live(key, number)) return;
      const payload = validateOwner(validateStatus(response.data), snapshot, true);
      setState({ owner: key, payload }); setError(previous => previous.startsWith("Aktion") ? previous : "");
      let dismissed = dismissedInMemory.current.has(dismissalKey(snapshot));
      try { dismissed = dismissed || sessionStorage.getItem(dismissalKey(snapshot)) === "dismissed"; } catch (_) { /* storage optional */ }
      if (!payload.required || !pendingContracts(payload).length) setOpen(false);
      else if (!dismissed && !page) setOpen(true);
    } catch (_) {
      if (live(key, number)) setError("Vertragsstatus derzeit nicht verfügbar. Bitte erneut prüfen.");
    }
  }, [page]); // Request bodies use the current captured login, never query-string credentials.

  useEffect(() => {
    mounted.current = true;
    let lastOwner = authKey(authSnapshot()); let lastDay = zurichDay();
    refresh(true);
    function sync() {
      const next = authKey(authSnapshot()); const day = zurichDay();
      const changed = next !== lastOwner || day !== lastDay;
      if (next !== lastOwner) {
        request.current += 1; lastOwner = next; setOwner(next); setState(null); setOpen(false); setError(""); setDelivery(null); clearPreview();
        lastRefresh.current = 0;
        busyRef.current = false; setBusy(false);
      }
      if (document.visibilityState !== "hidden") { lastDay = day; refresh(changed); }
    }
    const timer = setInterval(() => {
      if (authKey(authSnapshot()) !== lastOwner || zurichDay() !== lastDay) sync();
    }, 1000);
    window.addEventListener("focus", sync); window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      mounted.current = false; request.current += 1; clearInterval(timer);
      window.removeEventListener("focus", sync); window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", sync);
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    };
  }, [refresh, clearPreview]);

  function mutationKey(snapshot, row, action) {
    const key = `${dismissalKey(snapshot)}:${action}:${row.kind}:${row.version}:${row.draft_id || "new"}:${row.document_sha256 || "new"}`;
    let value = mutationKeys.current.get(key);
    try { value = value || sessionStorage.getItem(key); } catch (_) { /* storage optional */ }
    if (!value) {
      value = crypto.randomUUID();
      try { sessionStorage.setItem(key, value); } catch (_) { /* storage optional */ }
    }
    mutationKeys.current.set(key, value);
    return value;
  }
  async function mutate(action, row) {
    const snapshot = authSnapshot(); const key = authKey(snapshot);
    if (!snapshot || busyRef.current || state?.owner !== key || !validState?.required) return;
    if (action === "sign-test" && (!signature || !consent || preview?.draft_id !== row.draft_id || preview?.sha !== row.document_sha256)) return;
    busyRef.current = true; setBusy(true); setError(""); const number = ++request.current;
    try {
      const data = action === "start-test" ? { kind: row.kind } : { draft_id: row.draft_id, document_sha256: row.document_sha256, ...(validState.is_test ? { consent_demo: true } : { consent: true }), signature_png: signature };
      const response = await post(action, snapshot, { ...data, csrf: validState.csrf, idempotency_key: mutationKey(snapshot, row, action) });
      if (!live(key, number)) return;
      const result = response.data;
      if (!result || result.success !== true || result.mode !== "SIMPLE_SIGNATURE" || result.is_test !== validState.is_test || result.qes !== false || result.assurance !== "simple_electronic_signature" || result.legal_completion !== false) throw new Error("unavailable");
      validateOwner(result, snapshot);
      if (action === "sign-test") setDelivery({ owner: key, draft_id: row.draft_id, state: result.email_delivery });
      clearPreview();
    } catch (_) {
      if (live(key, number)) setError("Aktion nicht bestätigt. Status erneut prüfen; keine automatische Wiederholung.");
    } finally {
      if (live(key, number)) { busyRef.current = false; setBusy(false); await refresh(true); }
    }
  }
  async function loadDocument(row, download = false) {
    const snapshot = authSnapshot(); const key = authKey(snapshot);
    if (!snapshot || busyRef.current || state?.owner !== key || !row.draft_id) return;
    busyRef.current = true; setBusy(true); setError(""); const number = ++request.current;
    try {
      const response = await post("download", snapshot, { draft_id: row.draft_id, csrf: validState.csrf }, { responseType: "blob" });
      if (!live(key, number)) return;
      const blob = response.data;
      if (!(blob instanceof Blob) || blob.size === 0 || blob.size > 5000000) throw new Error("document");
      const bytes = await blob.arrayBuffer();
      const digest = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))).map(byte => byte.toString(16).padStart(2, "0")).join("");
      if (!live(key, number)) return;
      if (digest !== row.document_sha256) throw new Error("document");
      // PDF bytes are verified; the matching server DTO's DEMO text is rendered as text only.
      const mime = blob.type.split(";")[0].toLowerCase();
      if (download) {
        if (!["text/plain", "application/pdf"].includes(mime)) throw new Error("document");
        const url = URL.createObjectURL(blob); const link = document.createElement("a");
        link.href = url; link.download = `${validState.is_test ? "TEST-" : "Vertrag-"}${row.kind}.${mime === "application/pdf" ? "pdf" : "txt"}`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        if (mime !== "application/pdf" || typeof row.preview_text !== "string" || !row.preview_text.trim() || (validState.is_test && !/DEMO|TEST/.test(row.preview_text))) throw new Error("preview");
        setPreview({ draft_id: row.draft_id, sha: digest, text: row.preview_text }); setConsent(false); setSignature(null);
      }
    } catch (_) {
      if (live(key, number)) setError("Dokument konnte nicht sicher geprüft werden. Bitte erneut prüfen oder den Empfang kontaktieren.");
    } finally { if (live(key, number)) { busyRef.current = false; setBusy(false); } }
  }

  // Scope and rollout come only from the authenticated server response.
  if (!auth) return null;
  const blocked = validState?.blocked_kinds || [];
  const visible = owner === currentOwner && (validState?.ui_enabled === true || blocked.length > 0);
  const isTest = validState?.is_test === true;
  const suffix = isTest ? " · TEST" : "";
  const content = <>
    <p className="digital-contract-demo"><strong>{isTest ? DEMO : "Einfache elektronische Signatur – keine qualifizierte elektronische Signatur (QES)."}</strong></p>
    <p>{isTest ? "Eigener Kontotest für Amateur, SemiPro und Lehrpersonen. " : ""}Zahlungen, Absenzen und andere App-Funktionen bleiben erreichbar.</p>
    {error && <p role="alert">{error}</p>}
    {blocked.length > 0 && <p role="status">Noch keine freigegebene Vorlage verfügbar: {blocked.map(kind => KINDS[kind]).join(", ")}. Der Vertragsvorgang ist noch nicht vollständig. Du kannst die anderen App-Funktionen weiter nutzen.</p>}
    {delivery?.owner === currentOwner && !visible && <p role="status">{deliveryMessage(delivery.state)}</p>}
    <button type="button" disabled={busy} onClick={() => refresh(true)}>Status erneut prüfen</button>
    {visible && <ul className="digital-contract-list">{validState.contracts.map(row => <li key={row.kind}>
      <h3>{KINDS[row.kind]}{suffix}</h3><p>{LABELS[row.stage]}</p><small>Version: {row.version}</small>
      {row.stage === "required" && <button type="button" disabled={busy} onClick={() => mutate("start-test", row)}>{isTest ? "TEST-Entwurf erstellen" : "Vertrag vorbereiten"}</button>}
      {row.draft_id && <button type="button" disabled={busy} onClick={() => loadDocument(row, true)}>{isTest ? "TEST-Dokument herunterladen" : "Vertrag herunterladen"}</button>}
      {row.stage === "draft" && <>
        <button type="button" disabled={busy} onClick={() => loadDocument(row)}>Entwurf hier lesen</button>
        {preview?.draft_id === row.draft_id && <>
          <pre className="digital-contract-preview" tabIndex={0}>{preview.text}</pre>
          <SignaturePad key={`${preview.draft_id}:${preview.sha}`} onChange={setSignature} disabled={busy} isTest={isTest} />
          <label><input type="checkbox" checked={consent} disabled={busy} onChange={event => setConsent(event.target.checked)} /> Ich habe den angezeigten Vertrag gelesen und stimme ihm mit meiner gezeichneten einfachen elektronischen Unterschrift zu{isTest ? " – ausschliesslich für diesen eigenen TEST" : ""}. Ich weiss, dass dies keine QES ist.</label>
          <button type="button" disabled={busy || !consent || !signature} onClick={() => mutate("sign-test", row)}>{isTest ? "TEST: " : ""}Unterschrift speichern und zur Freigabe senden</button>
        </>}
      </>}
      {(row.stage === "approval_pending" || (row.stage === "draft" && delivery?.owner === currentOwner && delivery.draft_id === row.draft_id)) && <p role="status">{deliveryMessage(row.delivery_state == null && delivery?.owner === currentOwner && delivery.draft_id === row.draft_id ? delivery.state : row.delivery_state)}</p>}
      {row.stage === "approved_test" && <p>Test abgeschlossen – einfache elektronische Signatur. Die endgültige Vertragsfreigabe ausserhalb des Tests ist damit nicht bestätigt.</p>}
    </li>)}</ul>}
    {page && !visible && !error && <p>{validState ? "Für dieses Konto liegt derzeit kein angeforderter Vertrag vor." : "Vertragsstatus wird geprüft."}</p>}
  </>;
  if (page) return <section className="digital-contract-page" aria-label="Meine Verträge"><h1>Meine Verträge{suffix}</h1>{content}</section>;
  return <>
    {!visible && error && String(auth.login_user_id) === "80" && <aside className="digital-contract-notice"><p role="status">Vertragsstatus derzeit nicht verfügbar.</p><button type="button" disabled={busy} onClick={() => refresh(true)}>Status erneut prüfen</button><Link to="/Home/profile/contracts">Meine Verträge</Link></aside>}
    {visible && <aside className="digital-contract-notice"><Link to="/Home/profile/contracts">Meine Verträge{suffix}</Link>{blocked.length > 0 && <span>Vertragsvorlage noch in Vorbereitung.</span>}{pendingContracts(validState).length > 0 && <button type="button" onClick={() => setOpen(true)}>{isTest ? "TEST-Vertrag ansehen" : "Vertrag ansehen"}</button>}</aside>}
    {visible && open && <DemoDialog onClose={close} isTest={isTest}>{content}</DemoDialog>}
  </>;
}
